<template>
  <div v-if="isLoading" class="flex_horizontal justify-center ga-8 navbar"></div>
  <div class="flex_horizontal justify-center ga-8 navbar" v-else-if="showNavBar">
    <NavButton
      v-for="(link, index) in currentLinks"
      :key="link?.title ?? index"
      :icon="link?.icon"
      :separator="link?.separator"
      :title="link?.title"
      :path="link?.path"
      :id="createId(link?.title)"
      :isActive="link?.isSelected"
      :subItems="link?.subItems"
      :notification-count="link?.notificationCount"
      @item-click="itemClick"
    />
  </div>
  <div v-else class="backToHome__background">
    <div class="backToHome py-6">
      <a href="/dashboard/" class="d-flex align-center backToHome__action">
        <SvgFactory name="icon_arrows_expand_left" :width="18" class="mr-2" customColor="#475569" />
        Back to home</a
      >
    </div>
  </div>
</template>

<script>
import SvgFactory from '@design_system_v2/atoms/SvgFactory.vue';
import { tracker } from '@services';
import { mapGetters } from 'vuex';
import NavButton from './NavButton.vue';
import NoSchedulerChip from './NoSchedulerChip.vue';

const types = {
  MANAGER: 1,
  ALLOCATOR: 2,
  ICONNECTIONS: 4,
};
export default {
  name: 'NavBar',
  components: {
    NavButton,
    SvgFactory,
  },
  emits: ['item-click'],
  computed: {
    ...mapGetters('main', ['navInfo', 'featureFlags', 'infoUser', 'stayUpdatedData']),
    showNavBar() {
      return this.currentLinks.some((link) => !!link.isSelected);
    },
    meetingsTodayCount() {
      const meetingsToday = this.stayUpdatedData.find((item) => item.type === 'pending_meetings');
      return meetingsToday ? meetingsToday.count : 0;
    },
    currentLinks() {
      return this.linksV2;
    },
    isLoading() {
      return !this.featureFlags?.isLoaded;
    },
    linksV2() {
      const currentPath = this.$route.path;

      const funds = {
        items: this.handleLinksV2('Funds', 'links'),
        footerItems: this.handleLinksV2('Funds', 'footer'),
      };
      const allocators = {
        items: this.handleLinksV2('Allocators', 'links'),
        footerItems: this.handleLinksV2('Allocators', 'footer'),
      };
      const serviceProviders = {
        items: this.handleLinksV2('Service Providers', 'links'),
        footerItems: this.handleLinksV2('Service Providers', 'footer'),
      };
      const contacts = {
        items: this.handleLinksV2('Contacts', 'links'),
        footerItems: this.handleLinksV2('Contacts', 'footer'),
      };
      let searchForItems = [
        { title: 'Funds', path: '/list/funds', subItems: [...funds.items, ...funds.footerItems] },
        {
          title: 'Allocators',
          path: '/list/allocators',
          subItems: [...allocators.items, ...allocators.footerItems],
        },
        {
          title: 'Contacts',
          path: '/list/contacts',
          subItems: [...contacts.items, ...contacts.footerItems],
        },
        {
          title: 'Service Providers',
          path: '/list/service-providers',
          subItems: [...serviceProviders.items, ...serviceProviders.footerItems],
        },
      ];
      const isManager = this.infoUser.CompanyTypeID === types.MANAGER;
      searchForItems = searchForItems.filter((item) => {
        if (isManager && item.title === 'Funds') {
          return false;
        }
        const hasTitle = this.hasNavInfoMainOptions(item.title);
        if (!item.subItems?.length && !hasTitle) {
          return false;
        }
        return true;
      });
      const aiSubItems = [];

      if (this.featureFlags.main?.ai_chat) {
        aiSubItems.push({
          parentName: 'iConnections AI',
          title: 'AI Chat',
          path: '/ai',
        });
      }

      const data = [
        {
          title: 'Home',
          isSelected: currentPath === '/' || currentPath.startsWith('/dashboard'),
          path: '/',
          visible: true,
          icon: 'home',
        },
        {
          title: 'Events',
          visible: true,
          path: '/event',
          isSelected: currentPath.startsWith('/event'),
          subItems: [
            {
              parentName: 'Events',
              title: 'Upcoming',
              path: '/event/list/upcoming',
            },
            {
              parentName: 'Events',
              title: 'Registered',
              path: '/event/list/registered',
            },
            {
              parentName: 'Events',
              title: 'Past',
              path: '/event/list/past',
            },
            {
              parentName: 'Events',
              title: 'Meetups',
              path: this.featureFlags?.main?.create_meetup ? '/event/meetups' : '/coffee_chats',
            },
          ],
          icon: 'confirmation_number',
        },
        {
          title: 'Search for',
          visible: true,
          isSelected:
            currentPath.startsWith('/list') || currentPath.startsWith('/service_providers'),
          subItems: searchForItems,
          icon: 'person_search',
        },
        {
          title: 'Content',
          visible: true,
          path: '/content',
          isSelected: currentPath.startsWith('/content'),
          icon: 'video_library',
        },
        {
          title: 'Roadshows',
          visible: this.roadshowNestedLinks.length > 0,
          isSelected: currentPath.startsWith('/roadshows'),
          icon: 'flight',
          subItems: this.roadshowNestedLinks,
        },
        {
          title: this.infoUser.CompanyTypeID === types.MANAGER ? 'My Documents' : 'Investor Portal',
          isSelected: currentPath.startsWith('/documents'),
          path: '/documents',
          visible: this.renderDocuments,
          icon: 'folder_open',
        },
        {
          title: 'iConnections AI',
          isSelected: currentPath.startsWith('/vault') || currentPath.startsWith('/ai'),
          visible: this.featureFlags.main?.vault || this.featureFlags.main?.ai_chat,
          icon: 'auto_awesome',
          subItems: aiSubItems,
        },
        {
          title: 'Transactions',
          isSelected: currentPath.startsWith('/transactions'),
          path: '/transactions',
          visible: this.featureFlags.main?.transaction_navbar,
          icon: 'show_chart',
        },
        {
          separator: true,
          visible: true,
        },
        {
          title: 'Meetings',
          isSelected: currentPath.startsWith('/meetings'),
          path: '/inbox',
          visible: true,
          icon: 'calendar_month',
          notificationCount: this.meetingsTodayCount,
        },
      ];
      return data.filter((link) => link.visible);
    },
    renderDocuments() {
      if (!this.infoUser?.subscriber) {
        return false;
      }

      const DOCUMENTS_ACCESS_TYPES = [types.MANAGER, types.ALLOCATOR, types.ICONNECTIONS];

      return DOCUMENTS_ACCESS_TYPES.includes(this.infoUser.CompanyTypeID);
    },
    roadshowNestedLinks() {
      const links = [{ title: 'Upcoming roadshows', path: '/roadshows' }];
      const showMyRoadshow = this.featureFlags?.all?.my_roadshow;
      if (showMyRoadshow) {
        links.push({ title: 'My roadshows', path: '/roadshows/my-roadshows/upcoming' });
        links.push({ title: 'Create a roadshow', path: '/roadshows/create' });
      }

      return links;
    },
  },
  methods: {
    handleLinks(title, type) {
      const currentOptions = this.navInfo.mainOptions.find((item) => item.title === title);
      if (!currentOptions?.navOptions) {
        return [];
      }

      return currentOptions?.navOptions
        ?.filter((item) => {
          if (type === 'footer') {
            return item.eventId === 0;
          }
          return item.eventId !== 0;
        })
        ?.map((option) => {
          const formattedPath = `/${currentOptions.title.toLowerCase().replace(/\s/g, '_')}/list`;
          const event = option.eventId === 0 ? '' : `?eventId=${option.eventId}`;
          return {
            title: option.optionTitle,
            path: `${formattedPath}${event}`,
            parentName: title,
          };
        });
    },
    hasNavInfoMainOptions(title) {
      return this.navInfo.mainOptions.some((item) => item.title === title);
    },
    handleLinksV2(title, type) {
      const currentOptions = this.navInfo.mainOptions.find((item) => item.title === title);
      if (!currentOptions?.navOptions) {
        return [];
      }

      if (currentOptions.navOptions.length === 1 && currentOptions.navOptions?.[0]?.eventId === 0) {
        return [];
      }

      return currentOptions?.navOptions
        ?.filter((item) => {
          if (type === 'footer') {
            return item.eventId === 0;
          }
          return item.eventId !== 0;
        })
        ?.map((option) => {
          let formattedPath = '';
          formattedPath = `/list/${currentOptions.title.toLowerCase().replace(/\s/g, '-')}`;
          const event = option.eventId === 0 ? '' : `?eventId=${option.eventId}`;
          return {
            title: option.optionTitle,
            path: `${formattedPath}${event}`,
            parentName: title,
            disabled: option.isSchedulerOpened === false && option.eventId !== 0,
            leadingComponent:
              option.isSchedulerOpened === false && option.eventId !== 0
                ? NoSchedulerChip
                : undefined,
          };
        });
    },
    itemClick(item) {
      tracker('navbar_click', {
        page: item.parentName ?? item.title,
        subMenu: item.parentName ? item.title : null,
      });
      if (item?.path === '/meetings') {
        tracker('navbar_meetings_clicks');
      }
      this.$emit('item-click', item);
    },
    createId(title) {
      if (!title) {
        return 'nav_link-separator';
      }
      return `nav_link-${title
        .replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map((word) => word.toLowerCase())
        .join('_')}`;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@foundation';

.navbar {
  background: white;
  border-bottom: 1px solid map-get($neutral, 'neutral-100');
}
.backToHome {
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;
  &__background {
    background: white;
  }
  &__action {
    color: map-get($neutral, 'neutral-600');
  }
}
</style>
